import CssBaseline from "@mui/material/CssBaseline";
import Drawer from "@mui/material/Drawer";
import { CSSObject, styled, Theme } from "@mui/material/styles";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { ReactComponent as CollapseIcon } from "components/Icons/collapse.svg";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { selectInformationsHeight } from "reduxState/informations/informationsSlice";
import { selectIsRTL } from "reduxState/RTL/RTLSlice";
import {
  selectIsSidebarOpen,
  setIsSidebarOpen,
} from "reduxState/sidebar/sidebarSlice";
import { LinksList } from "./LinksList/LinksList";
import { Search } from "./Search/Search";
import { StoreSelect } from "./StoreSearch/StoreSelect";

export const drawerWidth = 248;
export const closedDrawerWidth = 80;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
  width: closedDrawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
});

const StyledDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

const useStyles = makeStyles<
  OLTheme,
  { isSidebarOpen: boolean; informationsHeight: number }
>((theme: OLTheme) =>
  createStyles({
    drawer: {
      flexShrink: 0,
      width: drawerWidth,
    },
    docked: {
      overflowX: "visible",
      overflow: "visible",
    },
    drawerPaper: {
      marginTop: (props) => `${props.informationsHeight}px`,
      height: (props) => `calc(100vh - ${props.informationsHeight}px)`,
      overflow: "visible",
      overflowX: "visible !important" as "visible",
      borderRight: "1px solid #ECECEC",
      backgroundColor: "#F9F9F9",
      border: 0,
      width: drawerWidth,
      // firefox
      scrollbarColor: `#17132D ${theme.primary700}`,
      scrollbarWidth: "thin",
      // chrome, safari
      "&::-webkit-scrollbar-button": {
        display: "none",
      },
      "&::-webkit-scrollbar": {
        backgroundColor: theme.primary700,
        width: "10px",
      },
      "&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb": {
        borderRadius: 8,
        backgroundColor: "#17132D",
      },
    },
    toolbar: {
      minHeight: 0,
    },
    logoWrapper: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    collapseWrapper: {
      position: "absolute",
      zIndex: "12",
      right: "-9px",
      top: "20px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "white",
      width: "18px",
      minWidth: "18px",
      minHeight: "18px",
      height: "18px",
      border: "1px solid #E5E5E5",
      borderRadius: "4px",
      cursor: "pointer",

      "& svg": {
        transform: ({ isSidebarOpen }) =>
          `rotate(${isSidebarOpen ? "0" : "180deg"})`,
        color: theme.black,
        width: "12px",
        height: "12px",
      },
    },
    searchWrapper: {
      padding: theme.spacing(0, 8),
      margin: (props) =>
        props.isSidebarOpen ? theme.spacing(4, 0) : theme.spacing(4, 0, 3),
    },
  }),
);

export function SidebarDrawer() {
  const dispatch = useAppDispatch();
  const isRTL = useAppSelector(selectIsRTL).isRTL;
  const isSidebarOpen = useAppSelector(selectIsSidebarOpen);
  const informationsHeight = useAppSelector(selectInformationsHeight);
  const classes = useStyles({ isSidebarOpen, informationsHeight });
  const handleCollapseMenuClick = () => {
    dispatch(setIsSidebarOpen(!isSidebarOpen));
  };

  return (
    <>
      <CssBaseline />
      <StyledDrawer
        classes={{ paper: classes.drawerPaper, docked: classes.docked }}
        open={isSidebarOpen}
        variant="permanent"
        anchor={isRTL ? "right" : "left"}
      >
        <div className={classes.toolbar} />
        <div
          className={classes.collapseWrapper}
          onClick={handleCollapseMenuClick}
        >
          <CollapseIcon />
        </div>
        <StoreSelect isDrawerOpen={isSidebarOpen} />
        <div className={classes.searchWrapper}>
          <Search isDrawerOpen={isSidebarOpen} />
        </div>
        <LinksList
          isDrawerOpen={isSidebarOpen}
          onCollapseMenuClick={handleCollapseMenuClick}
        />
      </StyledDrawer>
    </>
  );
}
